import React, { useEffect, useState } from 'react';
import {
  Popover, Box, Typography, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip,
} from '@mui/material';
import evaluationsApi, { ApprovedPriceHistoryItem, RequestedPriceHistoryItem } from '../services/evaluationService';
import { fmtMoney } from '../utils/formatMoney';

const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString() : '-';

interface ApprovedHistoryPopoverProps {
  type: 'approved';
  anchorEl: HTMLElement | null;
  sku: string;
  onClose: () => void;
}

interface RequestedHistoryPopoverProps {
  type: 'requested';
  anchorEl: HTMLElement | null;
  sku: string;
  onClose: () => void;
}

type PriceHistoryPopoverProps = ApprovedHistoryPopoverProps | RequestedHistoryPopoverProps;

const PriceHistoryPopover: React.FC<PriceHistoryPopoverProps> = ({ type, anchorEl, sku, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [approvedData, setApprovedData] = useState<ApprovedPriceHistoryItem[]>([]);
  const [requestedData, setRequestedData] = useState<RequestedPriceHistoryItem[]>([]);

  useEffect(() => {
    if (!anchorEl || !sku) return;
    setLoading(true);
    if (type === 'approved') {
      evaluationsApi.getApprovedHistory(sku).then(setApprovedData).catch(() => setApprovedData([])).finally(() => setLoading(false));
    } else {
      evaluationsApi.getRequestedHistory(sku).then(setRequestedData).catch(() => setRequestedData([])).finally(() => setLoading(false));
    }
  }, [anchorEl, sku, type]);

  const open = Boolean(anchorEl);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{ paper: { sx: { maxWidth: 520, maxHeight: 360 } } }}
    >
      <Box sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {type === 'approved' ? 'Approved Price History' : 'Requested Price History'}
          <Typography variant="caption" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
            ({sku})
          </Typography>
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : type === 'approved' ? (
          approvedData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No approved price history found.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Tier</TableCell>
                    <TableCell>Sales</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approvedData.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{fmtDate(row.approved_at)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{fmtMoney(row.approved_price)}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.company_name}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{row.company_tier}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{row.sales_person_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : (
          requestedData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No requested price history found.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Tier</TableCell>
                    <TableCell>Sales</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requestedData.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{fmtDate(row.requested_at)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{fmtMoney(row.requested_price)}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.company_name}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{row.company_tier}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{row.sales_person_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.status}
                          size="small"
                          color={row.status === 'REJECTED' ? 'error' : row.status === 'APPROVED' ? 'success' : 'default'}
                          sx={{
                            fontSize: '0.65rem', height: 20,
                            ...(row.status === 'REJECTED' ? { fontWeight: 700 } : {}),
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
      </Box>
    </Popover>
  );
};

export default PriceHistoryPopover;
